import React, { useState, useEffect, useRef } from 'react';
import { Card, Tabs, Modal, notification, Alert, Segmented, Switch, Button, Table, Tag } from 'antd';
import {
  TableOutlined,
  AppstoreOutlined,
  ExclamationCircleOutlined,
  PrinterOutlined,
  UserOutlined,
  ReloadOutlined,
  CreditCardOutlined
} from '@ant-design/icons';
import ordersService from '../../../services/ordersService';
import paymentService from '../../../services/paymentService';
import userRoleUtils from '../../../utils/userRoleUtils';
import OrderFilters from './OrderFilters';
import OrderCardList from './OrderCardList';
import OrderMetricsCards from '../../OrderMetricsCards';
import { ViewModal, StatusModal } from './modals';
import DateTimeModal from '../../DateTimeModal';
import OrderItemsEditModal from './modals/OrderItemsEditModal';
import OrderTable from './OrderTable';
import usePermission from '../../../hooks/usePermission';
import { useHistory } from 'react-router-dom';

import userService from '../../../services/userService';
import { blockChangeStatus } from '../../../utils/orderUtils';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { getSavedCardConfirmationCopy } from '../../../utils/orderPaymentAction';

const { TabPane } = Tabs;
const { confirm } = Modal;

// Calendar Icon component
const CalendarIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    stroke="currentColor"
    strokeWidth="2"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
    <path d="M8 14h.01"></path>
    <path d="M12 14h.01"></path>
    <path d="M16 14h.01"></path>
    <path d="M8 18h.01"></path>
    <path d="M12 18h.01"></path>
    <path d="M16 18h.01"></path>
  </svg>
);

const OrdersPage = () => {
  // User role state
  const isAdmin = userRoleUtils.isAdmin();
  const isImpersonating = userRoleUtils.isImpersonating();
  const showSchedulingWarnings = userRoleUtils.canViewSchedulingWarnings();
  const isAdminOrdersRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin/');
  const canPrintOrderLabels = isAdminOrdersRoute && (isAdmin || userRoleUtils.hasPermission('admin_view_orders'));
  const [users, setUsers] = useState([]);

  // Orders data state
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    total_orders: 0,
    total_active: 0,
    total_completed: 0,
    total_collected: 0,
    total_revenue: 0,
    average_order_value: 0,
    not_scheduled: 0
  });

  // Viewing options state
  const [viewType, setViewType] = useState('card');
  const [groupByDate, setGroupByDate] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  // Driver view state
  const [driverOrders, setDriverOrders] = useState([]);
  const [driverViewLoading, setDriverViewLoading] = useState(false);
  const [quantitySummary, setQuantitySummary] = useState([]);

  // ✅ NEW: Auto-refresh state for driver views
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const refreshIntervalRef = useRef(null);
  const lastOrderDataRef = useRef(null);

  // Date filter state
  const today = new Date().toISOString().split('T')[0];
  const [dateViewMode, setDateViewMode] = useState('day');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dateDisplayText, setDateDisplayText] = useState(new Date().toLocaleDateString());
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [customDatePickerVisible, setCustomDatePickerVisible] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        limit: 400
      };
      
      const response = await userService.getAllCustomers(params);
      setUsers(response.data || []);
   
    } catch (error) {
      notification.error({
        message: 'Error fetching users',
        description: error.message || 'Failed to load users'
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter and pagination state
  const [filters, setFilters] = useState({
    date_range: [today, today],
    order_status: null,
    payment_status: null
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0
  });
  const [sortInfo, setSortInfo] = useState({
    columnKey: 'property_date',
    order: 'descend'
  });
  const [searchText, setSearchText] = useState('');
  const [searchCategory, setSearchCategory] = useState('all');

  // Row selection state (for table view)
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkActionVisible, setBulkActionVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');

  // Modal states
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [dateTimeModalVisible, setDateTimeModalVisible] = useState(false);
  const [itemsEditModalVisible, setItemsEditModalVisible] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedOrderData, setSelectedOrderData] = useState(null);

  useEffect(() => {
    // Fetch users when component mounts
    fetchUsers();
  }, []);
  
  // Initialize date display text
  useEffect(() => {
    setDateDisplayText(new Date().toLocaleDateString());
  }, []);

  // Fetch orders whenever filters, search, sorting, tab, or pagination change
  useEffect(() => {
    const delay = searchText.trim().length >= 2 ? 300 : 0;
    const timer = setTimeout(() => {
      fetchOrders();
      if (activeTab === "driver_detailed" || activeTab === "driver_summary") {
        fetchDriverOrders();
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [filters, pagination.current, pagination.pageSize, sortInfo, activeTab, searchText, searchCategory]);

  // ✅ NEW: Auto-refresh effect for driver views
  useEffect(() => {
    // Only set up auto-refresh for driver views
    if ((activeTab === "driver_detailed" || activeTab === "driver_summary") && autoRefreshEnabled) {
      // Clear any existing interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }

      // Set up new interval - refresh every 10 seconds
      refreshIntervalRef.current = setInterval(() => {
        checkForDriverDataChanges();
      }, 10000);

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    } else {
      // Clear interval when not on driver views
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }
  }, [activeTab, autoRefreshEnabled, filters.date_range]);

  // ✅ NEW: Check for changes in driver data
  const checkForDriverDataChanges = async () => {
    try {
      const params = {
        page: 1,
        limit: 1000,
        calculate_metrics: false,
        sort_field: 'onfleet_status_time',
        sort_order: 'desc'
      };

      // Add date range params
      if (filters.date_range && filters.date_range.length === 2) {
        if (filters.date_range[0] === filters.date_range[1]) {
          params.property_date = filters.date_range[0];
        } else {
          params.start_date = filters.date_range[0];
          params.end_date = filters.date_range[1];
        }
      }

      const response = await ordersService.getAllOrdersAdmin(params);

      if (response && response.orders) {
        const ordersData = response.orders || [];
        
        // ✅ IMPROVED: Check for recent changes based on last refresh time or 5 minutes default
        const checkFromTime = lastRefreshTime 
          ? new Date(lastRefreshTime.getTime() - 1 * 60 * 1000) // 1 minute before last refresh for overlap
          : new Date(Date.now() - 5 * 60 * 1000); // 5 minutes ago if no previous refresh
        
        const hasRecentChanges = ordersData.some(order => {
          const statusTime = order.onfleet_status_time;
          if (!statusTime) return false;
          
          const statusDate = new Date(statusTime);
          return statusDate > checkFromTime;
        });

        // Check if driver assignments have changed
        const currentDriverData = ordersData.map(order => ({
          id: order.id,
          driver_name: order.driver_name,
          onfleet_status_time: order.onfleet_status_time,
          order_status: order.order_status
        }));

        const hasDriverChanges = lastOrderDataRef.current && 
          JSON.stringify(currentDriverData) !== JSON.stringify(lastOrderDataRef.current);

        if (hasRecentChanges || hasDriverChanges) {
          
          // Update the driver data
          fetchDriverOrders();
          
          // Show notification to user
         /*  notification.info({
            message: 'Driver Data Updated',
            description: 'Recent driver assignment changes detected. Data has been refreshed.',
            duration: 3,
            placement: 'topRight'
          }); */

          setLastRefreshTime(new Date());
        }

        // Store current data for comparison
        lastOrderDataRef.current = currentDriverData;
      }
    } catch (error) {
      console.error('Error checking for driver data changes:', error);
      // Silently fail to avoid spamming the user
    }
  };

  // ✅ NEW: Manual refresh function
  const handleManualRefresh = () => {
    if (activeTab === "driver_detailed" || activeTab === "driver_summary") {
      fetchDriverOrders();
      setLastRefreshTime(new Date());
      notification.success({
        message: 'Data Refreshed',
        description: 'Driver data has been manually refreshed.',
        duration: 2
      });
    } else {
      fetchOrders();
      notification.success({
        message: 'Data Refreshed',
        description: 'Orders data has been refreshed.',
        duration: 2
      });
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        order_status: filters.order_status,
        payment_status: filters.payment_status,
        calculate_metrics: true,
        sort_field: sortInfo.columnKey,
        sort_order: sortInfo.order === 'ascend' ? 'asc' : 'desc'
      };

      // Add search parameters
      const trimmedSearchText = searchText.trim();
      if (trimmedSearchText.length >= 2) {
        if (searchCategory === 'all') {
          params.search = trimmedSearchText;
        } else if (searchCategory === 'property') {
          params.search_property = trimmedSearchText;
        } else if (searchCategory === 'address') {
          params.search_address = trimmedSearchText;
        } else if (searchCategory === 'postcode') {
          params.search_postcode = trimmedSearchText;
        } else if (searchCategory === 'customer') {
          params.search_customer = trimmedSearchText;
        }
      }

      // Add date range params
      if (filters.date_range && filters.date_range.length === 2) {
        if (filters.date_range[0] === filters.date_range[1]) {
          params.property_date = filters.date_range[0];
        } else {
          params.start_date = filters.date_range[0];
          params.end_date = filters.date_range[1];
        }
      }

      const response = await ordersService.getAllOrdersAdmin(params);

      if (response && response.orders) {
        const ordersData = response.orders || [];
        setOrders(ordersData);

        setPagination(prev => ({
          ...prev,
          total: response.total || 0
        }));

        if (response.metrics) {
          setMetrics({
            ...response.metrics
          });
        }
      } else {
        setOrders(response || []);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      notification.error({
        message: 'Error fetching orders',
        description: error.message || 'Failed to load orders'
      });
    } finally {
      setLoading(false);
    }
  };

  // New function to fetch orders grouped by driver
  const fetchDriverOrders = async () => {
    try {
      setDriverViewLoading(true);
      const params = {
        page: 1,
        limit: 1000, // Get all orders for driver view
        calculate_metrics: false,
        sort_field: 'property_date', // Sort by date first
        sort_order: 'asc'
      };

      // Add date range params
      if (filters.date_range && filters.date_range.length === 2) {
        if (filters.date_range[0] === filters.date_range[1]) {
          params.property_date = filters.date_range[0];
        } else {
          params.start_date = filters.date_range[0];
          params.end_date = filters.date_range[1];
        }
      }

      const response = await ordersService.getAllOrdersAdmin(params);

      if (response && response.orders) {
        const ordersData = response.orders || [];
        
        // Process and group orders by driver
        const processedDriverOrders = processOrdersByDriver(ordersData);
        setDriverOrders(processedDriverOrders);
        
        // Process quantity summary
        const quantitySummaryData = processQuantitySummary(ordersData);
        setQuantitySummary(quantitySummaryData);

        // ✅ Store data for change detection
        const driverData = ordersData.map(order => ({
          id: order.id,
          driver_name: order.driver_name,
          onfleet_status_time: order.onfleet_status_time,
          order_status: order.order_status
        }));
        lastOrderDataRef.current = driverData;
      }
    } catch (error) {
      console.error("Error fetching driver orders:", error);
      notification.error({
        message: 'Error fetching driver orders',
        description: error.message || 'Failed to load driver orders'
      });
    } finally {
      setDriverViewLoading(false);
    }
  };

  // Process orders by driver and date, create flattened list for table
  const processOrdersByDriver = (orders) => {
    const driverGroups = {};
    
    // Group orders by driver first, then by date
    orders.forEach(order => {
      const driverKey = order.driver_name || 'Unassigned';
      const dateKey = order.property_date || 'No Date';
      
      if (!driverGroups[driverKey]) {
        driverGroups[driverKey] = {
          driverName: driverKey,
          driverPhone: order.driver_phone || '',
          dates: {},
          totalOrders: 0,
          totalItems: 0
        };
      }
      
      if (!driverGroups[driverKey].dates[dateKey]) {
        driverGroups[driverKey].dates[dateKey] = {
          date: dateKey,
          orders: [],
          dateOrderCount: 0,
          dateItemCount: 0
        };
      }
      
      driverGroups[driverKey].dates[dateKey].orders.push(order);
      driverGroups[driverKey].dates[dateKey].dateOrderCount++;
      driverGroups[driverKey].dates[dateKey].dateItemCount += order.order_products ? order.order_products.length : 0;
      driverGroups[driverKey].totalOrders++;
      driverGroups[driverKey].totalItems += order.order_products ? order.order_products.length : 0;
    });

    // Convert to flat array for table display
    const flatData = [];
    
    // Sort drivers - assigned drivers first (alphabetically), then Unassigned last
    const sortedDrivers = Object.entries(driverGroups).sort(([driverA], [driverB]) => {
      if (driverA === 'Unassigned' && driverB !== 'Unassigned') return 1;
      if (driverB === 'Unassigned' && driverA !== 'Unassigned') return -1;
      return driverA.localeCompare(driverB);
    });
    
    sortedDrivers.forEach(([driverKey, driverData]) => {
      // Add driver header row
      flatData.push({
        key: `driver-${driverKey}`,
        isDriverHeader: true,
        driverName: driverData.driverName,
        driverPhone: driverData.driverPhone,
        totalOrders: driverData.totalOrders,
        totalItems: driverData.totalItems,
        isUnassigned: driverKey === 'Unassigned'
      });

      // Sort dates chronologically
      const sortedDates = Object.entries(driverData.dates).sort(([dateA], [dateB]) => {
        return new Date(dateA) - new Date(dateB);
      });

      // Add date sections under each driver
      sortedDates.forEach(([dateKey, dateData]) => {
        // Add date header row
        flatData.push({
          key: `driver-${driverKey}-date-${dateKey}`,
          isDateHeader: true,
          driverName: driverData.driverName,
          orderDate: dateKey,
          dateOrderCount: dateData.dateOrderCount,
          dateItemCount: dateData.dateItemCount,
          isUnassigned: driverKey === 'Unassigned'
        });

        // Add order items under this date - sorted by arrival time ascending
        const allOrderItems = [];
        
        // Collect all order items for this date
        dateData.orders.forEach(order => {
          if (order.order_products && order.order_products.length > 0) {
            order.order_products.forEach((product, index) => {
              allOrderItems.push({
                order: order,
                product: product,
                arrivalTime: product.order_property_arrival_time || order.arrival_time || '00:00'
              });
            });
          } else {
            // Order without products
            allOrderItems.push({
              order: order,
              product: null,
              arrivalTime: order.arrival_time || '00:00'
            });
          }
        });

        // Sort all items by arrival time
        allOrderItems.sort((a, b) => {
          return a.arrivalTime.localeCompare(b.arrivalTime);
        });

        // Add sorted items to flat data
        allOrderItems.forEach(({ order, product }) => {
          if (product) {
            flatData.push({
              key: `${order.id}-${product.order_products_id}`,
              isDriverHeader: false,
              isDateHeader: false,
              orderStatus: order.order_status,
              propertyTitle: product.order_property_title,
              propertyAddress: order.delivery_address_1,
              postcode: order.delivery_zip_code,
              customerName: `${order.delivery_first_name || ''} ${order.delivery_last_name || ''}`.trim(),
              customerPhone: order.delivery_phone_no,
              productName: product.order_products_name,
              quantity: product.order_products_qty,
              price: product.order_products_price,
              arrivalTime: product.order_property_arrival_time,
              completionTime: product.order_property_completion_time,
              orderDate: order.property_date,
              trackingUrl: order.tracking_url,
              driverName: order.driver_name || 'Unassigned',
              driverPhone: order.driver_phone || '',
              taskShortId: order.task_short_id
            });
          } else {
            // Order without products
            flatData.push({
              key: `${order.id}-no-products`,
              isDriverHeader: false,
              isDateHeader: false,
              orderStatus: order.order_status,
              propertyTitle: order.order_property?.property_title || '',
              propertyAddress: order.delivery_address_1,
              postcode: order.delivery_zip_code,
              customerName: `${order.delivery_first_name || ''} ${order.delivery_last_name || ''}`.trim(),
              customerPhone: order.delivery_phone_no,
              productName: 'No items',
              quantity: 0,
              price: 0,
              arrivalTime: order.arrival_time,
              completionTime: order.completion_time,
              orderDate: order.property_date,
              trackingUrl: order.tracking_url,
              driverName: order.driver_name || 'Unassigned',
              driverPhone: order.driver_phone || '',
              taskShortId: order.task_short_id
            });
          }
        });
      });
    });

    return flatData;
  };

  // Process orders for quantity summary - grouped by driver, date, and item
  const processQuantitySummary = (orders) => {
    const quantityGroups = {};
    
    // Group by driver → date → product
    orders.forEach(order => {
      const driverKey = order.driver_name || 'Unassigned';
      const dateKey = order.property_date || 'No Date';
      
      if (!quantityGroups[driverKey]) {
        quantityGroups[driverKey] = {
          driverName: driverKey,
          driverPhone: order.driver_phone || '',
          dates: {},
          totalQuantity: 0
        };
      }
      
      if (!quantityGroups[driverKey].dates[dateKey]) {
        quantityGroups[driverKey].dates[dateKey] = {
          date: dateKey,
          products: {},
          dateQuantity: 0
        };
      }
      
      // Process order products
      if (order.order_products && order.order_products.length > 0) {
        order.order_products
          .filter(product => !String(product.order_products_name || '')
            .trim()
            .toLowerCase()
            .startsWith('cleaning service'))
          .forEach(product => {
          const productKey = product.order_products_name || 'Unknown Product';
          const quantity = parseFloat(product.order_products_qty || 0);
          
          if (!quantityGroups[driverKey].dates[dateKey].products[productKey]) {
            quantityGroups[driverKey].dates[dateKey].products[productKey] = {
              productName: productKey,
              quantity: 0
            };
          }
          
          quantityGroups[driverKey].dates[dateKey].products[productKey].quantity += quantity;
          quantityGroups[driverKey].dates[dateKey].dateQuantity += quantity;
          quantityGroups[driverKey].totalQuantity += quantity;
          });
      }
    });

    // Convert to flat array for table display
    const flatData = [];
    
    // Sort drivers - assigned drivers first (alphabetically), then Unassigned last
    const sortedDrivers = Object.entries(quantityGroups).sort(([driverA], [driverB]) => {
      if (driverA === 'Unassigned' && driverB !== 'Unassigned') return 1;
      if (driverB === 'Unassigned' && driverA !== 'Unassigned') return -1;
      return driverA.localeCompare(driverB);
    });
    
    sortedDrivers.forEach(([driverKey, driverData]) => {
      // Add driver header row
      flatData.push({
        key: `summary-driver-${driverKey}`,
        isDriverHeader: true,
        driverName: driverData.driverName,
        driverPhone: driverData.driverPhone,
        totalQuantity: driverData.totalQuantity,
        isUnassigned: driverKey === 'Unassigned'
      });

      // Sort dates chronologically
      const sortedDates = Object.entries(driverData.dates).sort(([dateA], [dateB]) => {
        return new Date(dateA) - new Date(dateB);
      });

      // Add date sections under each driver
      sortedDates.forEach(([dateKey, dateData]) => {
        // Add date header row
        flatData.push({
          key: `summary-driver-${driverKey}-date-${dateKey}`,
          isDateHeader: true,
          driverName: driverData.driverName,
          orderDate: dateKey,
          dateQuantity: dateData.dateQuantity,
          isUnassigned: driverKey === 'Unassigned'
        });

        // Add product items under this date - sorted by quantity descending
        const sortedProducts = Object.entries(dateData.products).sort(([, productDataA], [, productDataB]) => {
          return productDataB.quantity - productDataA.quantity; // Quantity descending
        });

        sortedProducts.forEach(([productKey, productData]) => {
          flatData.push({
            key: `summary-${driverKey}-${dateKey}-${productKey}`,
            isDriverHeader: false,
            isDateHeader: false,
            driverName: driverData.driverName,
            orderDate: dateKey,
            productName: productData.productName,
            quantity: productData.quantity,
            isUnassigned: driverKey === 'Unassigned'
          });
        });
      });
    });

    return flatData;
  };

  // Print driver orders
  const handlePrintDriverOrders = () => {
    const printContent = activeTab === "driver_summary" 
      ? generateQuantitySummaryPrintContent() 
      : generateDriverOrdersPrintContent();
    
    const printWindow = window.open('', '_blank', 'height=600,width=800');
    printWindow.document.write(`
      <html>
        <head>
          <title>${activeTab === "driver_summary" ? "Quantity Summary" : "Driver Orders"} Report</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 5px;
              line-height: 1.1;
              color: #333;
              font-size: 10px;
            }
            
            .print-header {
              text-align: center;
              margin-bottom: 8px;
              padding-bottom: 4px;
              border-bottom: 2px solid #001529;
            }
            
            .print-header h1 {
              color: #001529;
              margin-bottom: 3px;
              font-size: 16px;
            }
            
            .summary-info {
              font-size: 9px;
              line-height: 1.2;
            }
            
            .summary-info p {
              margin: 1px 0;
            }
            
            .driver-section {
              margin-bottom: 3px;
              break-inside: avoid;
            }
            
            .driver-header {
              background-color: #f0f0f0;
              padding: 4px 6px;
              margin-bottom: 2px;
              border-left: 3px solid #001529;
              break-inside: avoid;
            }
            
            .driver-header.unassigned-header {
              border-left-color: #ff4d4f;
              background-color: #fff2f0;
            }
            
            .driver-header h2 {
              color: #001529;
              margin-bottom: 1px;
              font-size: 12px;
            }
            
            .driver-header p {
              margin: 0;
              font-size: 9px;
              color: #666;
            }
            
            .date-subsection {
              margin-left: 8px;
              margin-bottom: 3px;
              break-inside: avoid;
            }
            
            .date-header {
              color: #52c41a;
              margin-bottom: 2px;
              font-size: 10px;
              padding: 2px 0;
              border-bottom: 1px solid #52c41a;
            }
            
            .orders-table,
            .summary-table {
              width: 100%;
              border-collapse: collapse;
              margin-left: 8px;
              margin-bottom: 3px;
              font-size: 8px;
            }
            
            .orders-table th,
            .orders-table td,
            .summary-table th,
            .summary-table td {
              border: 1px solid #ddd;
              padding: 1px 2px;
              text-align: left;
              vertical-align: top;
            }
            
            .orders-table th,
            .summary-table th {
              background-color: #f8f8f8;
              font-weight: bold;
              font-size: 8px;
            }
            
            .quantity-cell {
              font-weight: bold;
              color: #52c41a;
              text-align: center;
              width: 60px;
            }
            
            .summary-table .quantity-cell {
              text-align: right;
              padding-right: 5px;
            }
            
            @media print {
              body { 
                margin: 0;
                padding: 3px;
              }
              
              .print-header {
                margin-bottom: 5px;
                padding-bottom: 2px;
              }
              
              .driver-section {
                break-inside: avoid;
                margin-bottom: 2px;
              }
              
              .date-subsection {
                break-inside: avoid;
                margin-bottom: 2px;
              }
              
              .orders-table,
              .summary-table {
                break-inside: avoid;
                margin-bottom: 2px;
              }
              
              .orders-table tr,
              .summary-table tr {
                break-inside: avoid;
              }
              
              /* Force no page breaks after headers */
              .driver-header,
              .date-header {
                break-after: avoid;
              }
              
              /* Keep tables tight together */
              .orders-table tbody tr:last-child td,
              .summary-table tbody tr:last-child td {
                border-bottom: 1px solid #ddd;
              }
            }
            
            @page {
              margin: 0.2in;
              size: A4;
            }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            window.addEventListener('load', function() {
              setTimeout(function() {
                window.print();
                window.close();
              }, 500);
            });
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  // Generate print content for quantity summary
  const generateQuantitySummaryPrintContent = () => {
    const dateRange = filters.date_range;
    const dateStr = dateRange[0] === dateRange[1] 
      ? new Date(dateRange[0]).toLocaleDateString('en-GB')
      : `${new Date(dateRange[0]).toLocaleDateString('en-GB')} - ${new Date(dateRange[1]).toLocaleDateString('en-GB')}`;

    // Calculate totals
    const totalQuantity = quantitySummary
      .filter(item => !item.isDriverHeader && !item.isDateHeader)
      .reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
    const totalDrivers = quantitySummary.filter(item => item.isDriverHeader).length;
    const unassignedQuantity = quantitySummary
      .filter(item => !item.isDriverHeader && !item.isDateHeader && item.isUnassigned)
      .reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);

    // Group data by driver and date for printing
    const driverGroups = {};
    let currentDriver = null;
    let currentDate = null;

    quantitySummary.forEach(item => {
      if (item.isDriverHeader) {
        currentDriver = item.driverName;
        driverGroups[currentDriver] = {
          ...item,
          dates: {}
        };
      } else if (item.isDateHeader) {
        currentDate = item.orderDate;
        if (currentDriver && driverGroups[currentDriver]) {
          driverGroups[currentDriver].dates[currentDate] = {
            ...item,
            products: []
          };
        }
      } else {
        if (currentDriver && currentDate && 
            driverGroups[currentDriver] && 
            driverGroups[currentDriver].dates[currentDate]) {
          driverGroups[currentDriver].dates[currentDate].products.push(item);
        }
      }
    });

    let content = `<div class="print-header"><h1>Quantity Summary Report by Driver</h1><div class="summary-info"><p><strong>Date Range:</strong> ${dateStr}</p><p><strong>Summary:</strong> ${totalDrivers} Drivers | ${totalQuantity.toFixed(1)} Total Quantity | ${unassignedQuantity.toFixed(1)} Unassigned Quantity</p></div></div>`;

    // Sort drivers with Unassigned last
    const sortedDriverEntries = Object.entries(driverGroups).sort(([driverA], [driverB]) => {
      if (driverA === 'Unassigned' && driverB !== 'Unassigned') return 1;
      if (driverB === 'Unassigned' && driverA !== 'Unassigned') return -1;
      return driverA.localeCompare(driverB);
    });

    sortedDriverEntries.forEach(([driverName, driverData]) => {
      const isUnassigned = driverName === 'Unassigned';
      
      content += `<div class="driver-section"><div class="driver-header ${isUnassigned ? 'unassigned-header' : ''}"><h2>${isUnassigned ? '🚫 ' : '👤 '}${driverName} - Total: ${driverData.totalQuantity.toFixed(1)}</h2>${driverData.driverPhone ? `<p>Phone: ${driverData.driverPhone}</p>` : ''}</div>`;

      // Sort dates chronologically
      const sortedDates = Object.entries(driverData.dates).sort(([dateA], [dateB]) => {
        return new Date(dateA) - new Date(dateB);
      });

      sortedDates.forEach(([dateKey, dateData]) => {
        content += `<div class="date-subsection"><h3 class="date-header">📅 ${new Date(dateKey).toLocaleDateString('en-GB', {
          weekday: 'short',
          day: 'numeric',
          month: 'short'
        })} - Qty: ${dateData.dateQuantity.toFixed(1)}</h3><table class="summary-table"><thead><tr><th style="width: 75%;">Product</th><th style="width: 25%;">Quantity</th></tr></thead><tbody>`;

        dateData.products.forEach(product => {
          content += `<tr><td>${product.productName || ''}</td><td class="quantity-cell">${parseFloat(product.quantity).toFixed(1)}</td></tr>`;
        });

        content += `</tbody></table></div>`;
      });

      content += `</div>`;
    });

    return content;
  };

  // Generate print content for driver orders
  const generateDriverOrdersPrintContent = () => {
    const dateRange = filters.date_range;
    const dateStr = dateRange[0] === dateRange[1] 
      ? new Date(dateRange[0]).toLocaleDateString('en-GB')
      : `${new Date(dateRange[0]).toLocaleDateString('en-GB')} - ${new Date(dateRange[1]).toLocaleDateString('en-GB')}`;

    // Calculate totals
    const totalItems = driverOrders.filter(item => !item.isDriverHeader && !item.isDateHeader).length;
    const totalQuantity = driverOrders
      .filter(item => !item.isDriverHeader && !item.isDateHeader)
      .reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
    const totalDrivers = driverOrders.filter(item => item.isDriverHeader).length;
    const unassignedItems = driverOrders
      .filter(item => !item.isDriverHeader && !item.isDateHeader && item.driverName === 'Unassigned')
      .length;

    // Group data by driver and date for printing
    const driverGroups = {};
    let currentDriver = null;
    let currentDate = null;

    driverOrders.forEach(item => {
      if (item.isDriverHeader) {
        currentDriver = item.driverName;
        driverGroups[currentDriver] = {
          ...item,
          dates: {}
        };
      } else if (item.isDateHeader) {
        currentDate = item.orderDate;
        if (currentDriver && driverGroups[currentDriver]) {
          driverGroups[currentDriver].dates[currentDate] = {
            ...item,
            items: []
          };
        }
      } else {
        if (currentDriver && currentDate && 
            driverGroups[currentDriver] && 
            driverGroups[currentDriver].dates[currentDate]) {
          driverGroups[currentDriver].dates[currentDate].items.push(item);
        }
      }
    });

    let content = `<div class="print-header"><h1>Detailed Driver Orders Report</h1><div class="summary-info"><p><strong>Date Range:</strong> ${dateStr}</p><p><strong>Summary:</strong> ${totalDrivers} Drivers | ${totalItems} Items | ${totalQuantity.toFixed(1)} Total Quantity | ${unassignedItems} Unassigned Items</p></div></div>`;

    // Sort drivers with Unassigned last
    const sortedDriverEntries = Object.entries(driverGroups).sort(([driverA], [driverB]) => {
      if (driverA === 'Unassigned' && driverB !== 'Unassigned') return 1;
      if (driverB === 'Unassigned' && driverA !== 'Unassigned') return -1;
      return driverA.localeCompare(driverB);
    });

    sortedDriverEntries.forEach(([driverName, driverData]) => {
      const isUnassigned = driverName === 'Unassigned';
      
      content += `<div class="driver-section"><div class="driver-header ${isUnassigned ? 'unassigned-header' : ''}"><h2>${isUnassigned ? '🚫 ' : '👤 '}${driverName}</h2>${driverData.driverPhone ? `<p>Phone: ${driverData.driverPhone}</p>` : ''}<p>Orders: ${driverData.totalOrders} | Items: ${driverData.totalItems}</p></div>`;

      // Sort dates chronologically
      const sortedDates = Object.entries(driverData.dates).sort(([dateA], [dateB]) => {
        return new Date(dateA) - new Date(dateB);
      });

      sortedDates.forEach(([dateKey, dateData]) => {
        content += `<div class="date-subsection"><h3 class="date-header">📅 ${new Date(dateKey).toLocaleDateString('en-GB', {
          weekday: 'short',
          day: 'numeric',
          month: 'short'
        })} (${dateData.dateOrderCount} orders, ${dateData.dateItemCount} items)</h3><table class="orders-table"><thead><tr><th style="width: 15%;">Property</th><th style="width: 20%;">Address</th><th style="width: 15%;">Customer</th><th style="width: 20%;">Product</th><th style="width: 10%;">Qty</th><th style="width: 12%;">Time</th><th style="width: 8%;">Status</th></tr></thead><tbody>`;

        dateData.items.forEach(item => {
          const timeSlot = item.arrivalTime && item.completionTime 
            ? `${item.arrivalTime}-${item.completionTime}`
            : 'Not set';
            
          content += `<tr><td>${item.propertyTitle || ''}</td><td>${item.propertyAddress || ''}, ${item.postcode || ''}</td><td>${item.customerName || ''}</td><td>${item.productName || ''}</td><td class="quantity-cell">${item.quantity || ''}</td><td>${timeSlot}</td><td>${item.orderStatus || ''}</td></tr>`;
        });

        content += `</tbody></table></div>`;
      });

      content += `</div>`;
    });

    return content;
  };

  // Quantity summary table columns
  const quantitySummaryColumns = [
    {
      title: 'Driver/Date',
      dataIndex: 'driverName',
      key: 'driverName',
      width: 250,
      render: (text, record) => {
        if (record.isDriverHeader) {
          return (
            <div className="font-bold">
              <div className="flex items-center">
                {record.isUnassigned ? (
                  <Tag color="red" icon={<ExclamationCircleOutlined />}>
                    🚫 Unassigned (Total Qty: {record.totalQuantity.toFixed(1)})
                  </Tag>
                ) : (
                  <Tag color="blue" icon={<UserOutlined />}>
                    👤 {text} (Total Qty: {record.totalQuantity.toFixed(1)})
                  </Tag>
                )}
              </div>
              {record.driverPhone && (
                <div className="text-sm text-gray-500 mt-1">
                  📞 {record.driverPhone}
                </div>
              )}
            </div>
          );
        }
        if (record.isDateHeader) {
          return (
            <div className="ml-4 font-semibold">
              <div className="flex items-center">
                <Tag color="green" icon={<CalendarIcon />}>
                  📅 {new Date(record.orderDate).toLocaleDateString('en-GB', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short'
                  })} (Qty: {record.dateQuantity.toFixed(1)})
                </Tag>
              </div>
            </div>
          );
        }
        return null;
      }
    },
    {
      title: 'Product',
      dataIndex: 'productName',
      key: 'productName',
      width: 300,
      render: (text, record) => {
        if (record.isDriverHeader || record.isDateHeader) return null;
        return (
          <div className="ml-8">
            <div className="font-medium">{text}</div>
          </div>
        );
      }
    },
    {
      title: 'Total Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 150,
      render: (text, record) => {
        if (record.isDriverHeader || record.isDateHeader) return null;
        return (
          <div className="ml-8">
            <div className="text-2xl font-bold text-green-600">
              {parseFloat(text).toFixed(1)}
            </div>
          </div>
        );
      }
    }
  ];

  // Driver orders table columns
  const driverOrdersColumns = [
    {
      title: 'Driver/Date Info',
      dataIndex: 'driverName',
      key: 'driverName',
      width: 220,
      render: (text, record) => {
        if (record.isDriverHeader) {
          return (
            <div className="font-bold">
              <div className="flex items-center">
                {record.isUnassigned ? (
                  <Tag color="red" icon={<ExclamationCircleOutlined />}>
                    🚫 Unassigned ({record.totalOrders} orders, {record.totalItems} items)
                  </Tag>
                ) : (
                  <Tag color="blue" icon={<UserOutlined />}>
                    👤 {text} ({record.totalOrders} orders, {record.totalItems} items)
                  </Tag>
                )}
              </div>
              {record.driverPhone && (
                <div className="text-sm text-gray-500 mt-1">
                  📞 {record.driverPhone}
                </div>
              )}
            </div>
          );
        }
        if (record.isDateHeader) {
          return (
            <div className="ml-4 font-semibold">
              <div className="flex items-center">
                <Tag color="green" icon={<CalendarIcon />}>
                  📅 {new Date(record.orderDate).toLocaleDateString('en-GB', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short'
                  })} ({record.dateOrderCount} orders, {record.dateItemCount} items)
                </Tag>
              </div>
            </div>
          );
        }
        return null;
      }
    },
    {
      title: 'Property & Customer',
      dataIndex: 'propertyTitle',
      key: 'propertyTitle',
      width: 250,
      render: (text, record) => {
        if (record.isDriverHeader || record.isDateHeader) return null;
        return (
          <div className="ml-8">
            <div className="font-medium">{text}</div>
            <div className="text-sm text-gray-500">
              {record.propertyAddress}, {record.postcode}
            </div>
            <div className="text-sm text-blue-600">
              👤 {record.customerName}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Product & Quantity',
      dataIndex: 'productName',
      key: 'productName',
      width: 200,
      render: (text, record) => {
        if (record.isDriverHeader || record.isDateHeader) return null;
        return (
          <div className="ml-8">
            <div className="font-medium">{text}</div>
            <div className="text-lg font-bold text-green-600">
              Qty: {record.quantity}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Time Slot',
      dataIndex: 'arrivalTime',
      key: 'schedule',
      width: 150,
      render: (text, record) => {
        if (record.isDriverHeader || record.isDateHeader) return null;
        return (
          <div className="ml-8">
            <div className="text-sm font-medium">
              🕐 {record.arrivalTime || 'N/A'} - {record.completionTime || 'N/A'}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      width: 120,
      render: (text, record) => {
        if (record.isDriverHeader || record.isDateHeader) return null;
        
        let color = 'default';
        switch (text) {
          case 'Scheduled': color = 'blue'; break;
          case 'En-route': 
          case 'On-Route': color = 'orange'; break;
          case 'Completed': color = 'green'; break;
          case 'Collected': color = 'purple'; break;
          case 'Cancelled': color = 'red'; break;
          case 'Failed': color = 'volcano'; break;
        }
        
        return (
          <div className="ml-8">
            <Tag color={color}>{text === 'On-Route' || text === 'En-route' ? 'En-Route' : text}</Tag>
          </div>
        );
      }
    }
  ];

  // Handler functions
  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleSearchCategoryChange = (value) => {
    setSearchCategory(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleResetFilters = () => {
    const todayFormatted = new Date().toISOString().split('T')[0];

    setFilters({
      order_status: null,
      payment_status: null,
      date_range: [todayFormatted, todayFormatted]
    });
    setDateViewMode('day');
    setCurrentDate(new Date());
    setDateDisplayText(new Date().toLocaleDateString());
    setIsCustomRange(false);
    setSearchText('');
    setSearchCategory('all');
    setSortInfo({ columnKey: 'property_date', order: 'descend' });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({
      ...prev,
      current: page
    }));
  };

  const handleSortChange = (field, order) => {
    setSortInfo({
      columnKey: field,
      order: order
    });
  };

  const handleTableChange = (tablePagination, filters, sorter) => {
    setPagination(prev => ({
      ...prev,
      current: tablePagination.current,
      pageSize: tablePagination.pageSize
    }));

    if (sorter && sorter.columnKey) {
      setSortInfo({
        columnKey: sorter.columnKey,
        order: sorter.order
      });
    } else {
      setSortInfo({ columnKey: 'property_date', order: 'descend' });
    }
  };

  const handleExport = () => {
    exportToExcelFromTableData(orders, 'AllOrders', 'xlsx');
  };

  const handleExportCSV = () => {
    exportToExcelFromTableData(orders, 'AllOrders', 'csv');
  };

  // Handle date range change
  const handleDateRangeChange = (dateRange, viewMode, displayText, newDate) => {
    if (!dateRange || dateRange.length !== 2) return;

    if (newDate) {
      setCurrentDate(newDate);
    } else if (viewMode !== 'custom') {
      setCurrentDate(new Date(dateRange[0]));
    }

    setDateViewMode(viewMode);
    setDateDisplayText(displayText);

    setFilters(prev => ({
      ...prev,
      date_range: dateRange
    }));

    setPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  // Handle custom range flag
  const handleCustomRangeChange = (isCustom, dateRange) => {
    setIsCustomRange(isCustom);

    if (dateRange && dateRange.length === 2) {
      setFilters(prev => ({
        ...prev,
        date_range: dateRange
      }));
    }
  };

  // Delete order confirmation
  const showDeleteConfirm = (orderId) => {
    confirm({
      title: 'Are you sure you want to delete this order?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        handleDelete(orderId);
      }
    });
  };

  // Delete order
  const handleDelete = async (orderId) => {
    try {
      await ordersService.deleteOrder(orderId);
      notification.success({
        message: 'Order deleted successfully'
      });
      fetchOrders();
    } catch (error) {
      notification.error({
        message: 'Error deleting order',
        description: error.message || 'Failed to delete order'
      });
    }
  };

  // Bulk status change
  const handleBulkStatusChange = () => {
    if (!selectedStatus || selectedRowKeys.length === 0) {
      notification.warning({
        message: 'Please select a status and at least one order'
      });
      return;
    }

    const blocked = orders.filter(order => selectedRowKeys.includes(order.id) && blockChangeStatus(order.order_status));
    if (blocked.length > 0) {
      notification.warning({
        message: 'Some selected orders cannot be changed',
        description: `${blocked.length} orders are Completed, Collected, Cancelled or Failed and cannot be modified.`
      });
      return;
    }

    confirm({
      title: `Change ${selectedRowKeys.length} orders to "${selectedStatus}"?`,
      icon: <ExclamationCircleOutlined />,
      content: 'This will update the status for all selected orders.',
      onOk: async () => {
        try {
          setLoading(true);
          await ordersService.updateBulkOrderStatus(selectedRowKeys, selectedStatus);

          notification.success({
            message: 'Orders updated successfully',
            description: `${selectedRowKeys.length} orders have been updated to ${selectedStatus}`
          });

          setSelectedRowKeys([]);
          setSelectedStatus('');
          setBulkActionVisible(false);
          fetchOrders();
        } catch (error) {
          notification.error({
            message: 'Failed to update orders',
            description: error.message || 'An error occurred while updating orders'
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Modal handlers
  const handleViewOrder = (orderId) => {
    setSelectedOrderId(orderId);
    setViewModalVisible(true);
  };

  const handleUpdateStatus = (orderId) => {
    setSelectedOrderId(orderId);
    setStatusModalVisible(true);
  };

  const handleEditDateTime = (orderId, orderData) => {
    setSelectedOrderId(orderId);
    setSelectedOrderData(orderData);
    setDateTimeModalVisible(true);
  };

  const handleEditItems = (orderId) => {
    setSelectedOrderId(orderId);
    setItemsEditModalVisible(true);
  };

  const handleCaptureSavedCard = async (orderId, actionOrder = null) => {
    const order = actionOrder || orders.find(item => Number(item.id) === Number(orderId)) || {};
    const confirmationCopy = getSavedCardConfirmationCopy(order);
    return new Promise((resolve) => {
      confirm({
        title: confirmationCopy.title,
        icon: <CreditCardOutlined />,
        content: confirmationCopy.content,
        onOk: async () => {
          try {
            setLoading(true);
            const response = await paymentService.captureSavedCard(orderId);
            notification.success({
              message: 'Payment Captured',
              description: 'The client card was charged successfully.'
            });
            await fetchOrders();
            if (activeTab === "driver_detailed" || activeTab === "driver_summary") {
              await fetchDriverOrders();
            }
            resolve(response);
          } catch (error) {
            notification.error({
              message: 'Payment capture failed',
              description: error.message || 'Unable to charge the saved card.'
            });
            resolve(null);
          } finally {
            setLoading(false);
          }
        },
        onCancel: () => resolve(null)
      });
    });
  };

  const handleModalSuccess = () => {
    fetchOrders();
    
    // ✅ Also refresh driver data if we're on driver tabs
    if (activeTab === "driver_detailed" || activeTab === "driver_summary") {
      fetchDriverOrders();
    }
    
    closeModals();
  };

  const closeModals = () => {
    setViewModalVisible(false);
    setStatusModalVisible(false);
    setDateTimeModalVisible(false);
    setItemsEditModalVisible(false);
    setSelectedOrderId(null);
    setSelectedOrderData(null);
  };

  const exportToExcelFromTableData = (data, filename = 'Orders', format = 'xlsx') => {
    if (!data || data.length === 0) {
      notification.warning({ message: 'No data to export' });
      return;
    }

    const formattedData = data.map(order => ({
      ID: order.id,
      Customer: `${order.delivery_first_name || ''} ${order.delivery_last_name || ''}`,
      Property: order.order_property?.property_title || '',
      Address: order.order_property?.apartment_address || '',
      Postcode: order.order_property?.delivery_zip_code || '',
      Status: order.order_status || '',
      Payment: order.payment_status == '0' ? 'Pending' : 'Paid',
      Date: order.property_date || '',
      Total: order.order_total || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    // Auto width for Excel columns
    const maxWidths = Object.keys(formattedData[0]).map((key) =>
      Math.max(
        key.length,
        ...formattedData.map(row => String(row[key] || '').length)
      )
    );

    worksheet['!cols'] = maxWidths.map(w => ({ wch: w + 5 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');

    if (format === 'csv') {
      const csv = XLSX.utils.sheet_to_csv(worksheet);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    } else {
      const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
    }
  };

  // Get count of unscheduled orders
  const unscheduledOrdersCount = showSchedulingWarnings
    ? orders.filter(order => userRoleUtils.hasSchedulingIssue(order)).length
    : 0;

  return (
    <div className="p-3 sm:p-4 lg:p-6">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-xl sm:text-2xl font-bold">Orders</h1>
      </div>

      {/* Order Metrics */}
      <OrderMetricsCards metrics={metrics} showSchedulingWarnings={showSchedulingWarnings} />

      {/* Alert for unscheduled orders - only show for admin users */}
      {showSchedulingWarnings && unscheduledOrdersCount > 0 && (
        <Alert
          message="Scheduling Issues Detected"
          description={`${unscheduledOrdersCount} orders are not properly scheduled. These orders are missing task IDs and are marked with an orange warning icon.`}
          type="warning"
          showIcon
          className="mb-6"
        />
      )}

      <Card className="shadow-md mb-6 overflow-hidden" bodyStyle={{ padding: 16 }}>
        {/* Filters Component */}
        <OrderFilters
          dateViewMode={dateViewMode}
          currentDate={currentDate}
          dateRange={filters.date_range}
          dateDisplayText={dateDisplayText}
          isCustomRange={isCustomRange}
          orderStatusFilter={filters.order_status}
          paymentStatusFilter={filters.payment_status}
          searchText={searchText}
          searchCategory={searchCategory}
          sortInfo={sortInfo}
          onDateRangeChange={handleDateRangeChange}
          onCustomRangeChange={handleCustomRangeChange}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          onSearch={handleSearch}
          onSearchCategoryChange={handleSearchCategoryChange}
          onSortChange={handleSortChange}
          onExport={handleExport}
          onExportCSV={handleExportCSV}
          onRefresh={fetchOrders}
          loading={loading}
          isAdmin={isAdmin}
        />

        {/* View Toggle and Group By Toggle - Only show for regular orders tab */}
        {activeTab === "all" && (
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
            <div className="flex items-center">
              <Segmented
                options={[
                  {
                    value: 'table',
                    icon: <TableOutlined />,
                    label: 'Table'
                  },
                  {
                    value: 'card',
                    icon: <AppstoreOutlined />,
                    label: 'Card'
                  }
                ]}
                value={viewType}
                onChange={setViewType}
              />
            </div>
            
            <div className="flex items-center justify-between sm:justify-end">
              <span className="mr-2">Group by Date</span>
              <Switch 
                checked={groupByDate} 
                onChange={setGroupByDate}
              />
            </div>
          </div>
        )}

        {/* ✅ NEW: Auto-refresh controls and print button for driver views */}
        {(activeTab === "driver_detailed" || activeTab === "driver_summary") && (
          <div className="flex flex-col gap-3 lg:flex-row lg:justify-between lg:items-center mb-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
              {/* Auto-refresh toggle */}
              <div className="flex items-center space-x-2">
                <Switch 
                  checked={autoRefreshEnabled} 
                  onChange={setAutoRefreshEnabled}
                  size="small"
                />
                <span className="text-sm text-gray-600">Auto-refresh (10s)</span>
              </div>
              
              {/* Manual refresh button */}
              <Button
                icon={<ReloadOutlined />}
                onClick={handleManualRefresh}
                size="small"
                loading={driverViewLoading}
              >
                Refresh
              </Button>
              
              {/* Last refresh time */}
              {lastRefreshTime && (
                <span className="text-xs text-gray-500">
                  Last updated: {lastRefreshTime.toLocaleTimeString()}
                </span>
              )}
            </div>

            {/* Print button */}
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrintDriverOrders}
              loading={driverViewLoading}
              className="w-full sm:w-auto"
            >
              Print {activeTab === "driver_summary" ? "Quantity Summary" : "Detailed Orders"}
            </Button>
          </div>
        )}

        {/* Tab Navigation */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className="bg-white mb-4"
        >
          <TabPane tab="All Orders" key="all" />
          {isAdmin && 
          <>
          <TabPane tab="Quantity Summary by Driver" key="driver_summary" />
          <TabPane tab="Detailed Orders by Driver" key="driver_detailed" />
          </>}
        </Tabs>

        {/* Content Based on Active Tab */}
        {activeTab === "all" ? (
          // Regular orders view
          viewType === 'table' ? (
            <OrderTable
              orders={orders}
              loading={loading}
              pagination={pagination}
              sortInfo={sortInfo}
              onTableChange={handleTableChange}
              isAdmin={isAdmin}
              selectedRowKeys={selectedRowKeys}
              setSelectedRowKeys={setSelectedRowKeys}
              setBulkActionVisible={setBulkActionVisible}
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              onBulkStatusChange={handleBulkStatusChange}
              bulkActionVisible={bulkActionVisible}
              onViewDetails={handleViewOrder}
              onEditItems={handleEditItems}
              onEditDateTime={handleEditDateTime}
              onDelete={showDeleteConfirm}
              onCaptureSavedCard={handleCaptureSavedCard}
              onNoteSaved={fetchOrders}
              CalendarIcon={CalendarIcon}
              groupByDate={groupByDate}
              showSchedulingWarnings={showSchedulingWarnings}
            />
          ) : (
            <OrderCardList
              orders={orders}
              loading={loading}
              isAdmin={isAdmin}
              groupByDate={groupByDate}
              pagination={pagination}
              onViewDetails={handleViewOrder}
              onEditItems={handleEditItems}
              onEditDateTime={handleEditDateTime}
              onCaptureSavedCard={handleCaptureSavedCard}
              onNoteSaved={fetchOrders}
              onPageChange={handlePageChange}
              CalendarIcon={CalendarIcon}
              showSchedulingWarnings={showSchedulingWarnings}
            />
          )
        ) : activeTab === "driver_summary" ? (
          // Quantity Summary view
          <div>
            <div className="mb-4 bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-2">📊 Quantity Summary by Driver & Date</h3>
              <p className="text-sm text-gray-600">
                This view shows total quantities of each product grouped by driver and delivery date. 
                Perfect for logistics planning and inventory management.
                {autoRefreshEnabled && (
                  <span className="block mt-1 text-blue-600 font-medium">
                    🔄 Auto-refreshing every 10 seconds to detect driver assignment changes
                  </span>
                )}
              </p>
            </div>

            {/* Summary Cards for Quantity View */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {quantitySummary
                    .filter(item => !item.isDriverHeader && !item.isDateHeader)
                    .reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0).toFixed(1)}
                </div>
                <div className="text-sm text-gray-600">Total Quantity</div>
              </Card>
              
              <Card className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {quantitySummary.filter(item => item.isDriverHeader).length}
                </div>
                <div className="text-sm text-gray-600">Total Drivers</div>
              </Card>
              
              <Card className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {quantitySummary
                    .filter(item => !item.isDriverHeader && !item.isDateHeader && item.isUnassigned)
                    .reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0).toFixed(1)}
                </div>
                <div className="text-sm text-gray-600">Unassigned Quantity</div>
              </Card>
            </div>
            
            <Table
              columns={quantitySummaryColumns}
              dataSource={quantitySummary}
              loading={driverViewLoading}
              pagination={false}
              size="small"
              rowClassName={(record) => {
                if (record.isDriverHeader) {
                  return record.isUnassigned 
                    ? 'bg-red-50 font-bold' 
                    : 'bg-blue-50 font-bold';
                }
                if (record.isDateHeader) {
                  return 'bg-green-50 font-semibold';
                }
                return '';
              }}
              scroll={{ x: 800 }}
            />
            
            {quantitySummary.length === 0 && !driverViewLoading && (
              <div className="text-center py-8 text-gray-500">
                <UserOutlined className="text-4xl mb-2" />
                <p>No quantity data found for the selected date range.</p>
              </div>
            )}
          </div>
        ) : (
          // Detailed Driver orders view
          <div>
            <div className="mb-4 bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-2">📋 Detailed Order Items by Driver Assignment</h3>
              <p className="text-sm text-gray-600">
                This view shows all order details grouped by their assigned driver and delivery date. 
                Includes customer information, addresses, delivery times, and order status.
                {autoRefreshEnabled && (
                  <span className="block mt-1 text-blue-600 font-medium">
                    🔄 Auto-refreshing every 10 seconds to detect driver assignment changes
                  </span>
                )}
              </p>
            </div>

            {/* Summary Cards for Detailed View */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {driverOrders.filter(item => !item.isDriverHeader && !item.isDateHeader).length}
                </div>
                <div className="text-sm text-gray-600">Total Items</div>
              </Card>
              
              <Card className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {driverOrders
                    .filter(item => !item.isDriverHeader && !item.isDateHeader)
                    .reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0).toFixed(1)}
                </div>
                <div className="text-sm text-gray-600">Total Quantity</div>
              </Card>
              
              <Card className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {driverOrders.filter(item => item.isDriverHeader).length}
                </div>
                <div className="text-sm text-gray-600">Total Drivers</div>
              </Card>
              
              <Card className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {driverOrders
                    .filter(item => !item.isDriverHeader && !item.isDateHeader && item.driverName === 'Unassigned')
                    .length}
                </div>
                <div className="text-sm text-gray-600">Unassigned Items</div>
              </Card>
            </div>
            
            <Table
              columns={driverOrdersColumns}
              dataSource={driverOrders}
              loading={driverViewLoading}
              pagination={false}
              size="small"
              rowClassName={(record) => {
                if (record.isDriverHeader) {
                  return record.isUnassigned 
                    ? 'bg-red-50 font-bold' 
                    : 'bg-blue-50 font-bold';
                }
                if (record.isDateHeader) {
                  return 'bg-green-50 font-semibold';
                }
                return '';
              }}
              scroll={{ x: 1000 }}
            />
            
            {driverOrders.length === 0 && !driverViewLoading && (
              <div className="text-center py-8 text-gray-500">
                <UserOutlined className="text-4xl mb-2" />
                <p>No order items found for the selected date range.</p>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Modals */}
      <ViewModal
        visible={viewModalVisible}
        orderId={selectedOrderId}
        onCancel={closeModals}
        users={users}
        isAdmin={isAdmin}
        canPrintLabel={canPrintOrderLabels}
        onCaptureSavedCard={handleCaptureSavedCard}
      />

      {/* Admin-only modals */}
      {isAdmin && (
        <>
          <StatusModal
            visible={statusModalVisible}
            orderId={selectedOrderId}
            onCancel={closeModals}
            onSuccess={handleModalSuccess}
          />

          <DateTimeModal
            isOpen={dateTimeModalVisible}
            onClose={closeModals}
            propertyId={selectedOrderId}
            initialData={selectedOrderData}
            orderId={selectedOrderId}
            onSave={handleModalSuccess}
          />
          
          <OrderItemsEditModal
            visible={itemsEditModalVisible}
            orderId={selectedOrderId}
            onCancel={closeModals}
            onSuccess={handleModalSuccess}
          />
        </>
      )}
    </div>
  );
};

export default OrdersPage;
